import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import { api, apiErrorMessage } from "../lib/api";
import { Coins, Copy, AlertTriangle, RefreshCw, ChevronRight, ArrowLeft } from "lucide-react";
import { useI18n } from "../lib/i18n";

/**
 * Iter 92 — Streamer credit-purchase portal.
 *
 * The payment-reference concept was removed from the streamer-facing UI.
 * The transaction hash IS the anchor: the purchase can only complete once
 * the streamer enters the tx hash. Coins carry no memo/reference on-chain,
 * so the reference added no value and risked confusion.
 *
 *   Step A (pick coin)  → tile grid, one per configured coin/network
 *                          (GET /credit-topups/deposit-info — RECEIVING_WALLETS
 *                          as sole source of truth).
 *   Step B (deposit)    → per-coin deposit view: QR + address + red network
 *                          warning + destination_tag if config has one +
 *                          amount + tx-hash input. "Complete purchase" is
 *                          disabled until the tx hash is non-empty and
 *                          matches the network's mild plausibility pattern.
 *                          Client calls POST /credit-topups/init then
 *                          POST /credit-topups/{id}/submit in sequence.
 *
 * Terminology preserved: Credits / Platform Account entitlement, top-up =
 * credit purchase (never deposit-as-custody / escrow / wallet-balance).
 */
const NETWORK_CURRENCY = {
  "BTC": "BTC", "ETH": "ETH", "LTC": "LTC",
  "USDT-TRC20": "USDT", "USDT-ERC20": "USDT", "USDC-ERC20": "USDC",
  "XRP": "XRP", "TRX": "TRX", "SOL": "SOL",
};

// Client-side pre-flight plausibility — server enforces the real check. The
// button stays disabled until this passes so we don't fire the round-trip
// with obviously wrong input.
const HASH_PATTERNS = {
  ETH:        /^0x[0-9a-fA-F]{64}$/,
  "USDT-ERC20": /^0x[0-9a-fA-F]{64}$/,
  "USDC-ERC20": /^0x[0-9a-fA-F]{64}$/,
  BTC:        /^[0-9a-fA-F]{64}$/,
  LTC:        /^[0-9a-fA-F]{64}$/,
  "USDT-TRC20": /^[0-9a-fA-F]{64}$/,
  TRX:        /^[0-9a-fA-F]{64}$/,
  XRP:        /^[0-9a-fA-F]{64}$/,
  SOL:        /^[1-9A-HJ-NP-Za-km-z]{40,120}$/,
};

function CopyBtn({ text, testid, label = "Copy" }) {
  return (
    <button
      data-testid={testid}
      onClick={async () => { try { await navigator.clipboard.writeText(text); toast.success("Copied"); } catch { toast.error("Copy failed"); } }}
      className="inline-flex items-center gap-1 border border-white/20 px-2 py-1 text-[10px] font-mono uppercase tracking-widest hover:border-[#F5B841] hover:text-[#F5B841] transition-colors"
    ><Copy className="w-3 h-3" />{label}</button>
  );
}

function CoinPicker({ coins, onPick }) {
  const { t } = useI18n();
  if (coins.length === 0) {
    return (
      <div className="border border-[#F5B841]/40 bg-[#F5B841]/[0.05] px-3 py-2 text-xs text-[#F5B841]" data-testid="bc-no-coins">
        No coins are currently accepted. Please check back shortly — admin will enable one or more networks soon.
      </div>
    );
  }
  return (
    <div data-testid="bc-step-pick">
      <label className="block text-[10px] font-mono uppercase tracking-widest text-[#8B97AE] mb-2">{t("buyCredits.chooseCoin")}</label>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2" data-testid="bc-coin-grid">
        {coins.map((c) => (
          <button
            key={c.network}
            data-testid={`bc-coin-${c.network}`}
            onClick={() => onPick(c)}
            className="text-left border border-white/15 hover:border-[#F5B841] hover:bg-[#F5B841]/[0.04] px-3 py-3 transition-colors group"
          >
            <div className="flex items-center justify-between">
              <div className="font-mono text-sm text-[#E8ECF4]">{c.coin}</div>
              <ChevronRight className="w-3 h-3 text-[#8B97AE] group-hover:text-[#F5B841]" />
            </div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE] mt-1">{c.label}</div>
          </button>
        ))}
      </div>
    </div>
  );
}

function DepositAndSubmit({ coin, onBack, onDone }) {
  const [amount, setAmount] = useState("");
  const [hash, setHash] = useState("");
  const [busy, setBusy] = useState(false);
  const hasTag = coin.destination_tag !== null && coin.destination_tag !== undefined && String(coin.destination_tag).length > 0;
  const minUsd = Number(coin.min_usd || 5);

  const pattern = HASH_PATTERNS[coin.network];
  const hashOk = hash.trim().length > 0 && (!pattern || pattern.test(hash.trim()));
  const amountOk = Number(amount) > 0;

  const submit = async () => {
    if (!amountOk) { toast.error("Enter an amount greater than 0"); return; }
    if (!hashOk)   { toast.error("Enter the transaction hash your wallet gave you"); return; }
    setBusy(true);
    try {
      // Two-step server flow (unchanged): init then submit. The client
      // collects the hash upfront so both fire in one operator action.
      const { data: topup } = await api.post("/credit-topups/init", {
        amount: Number(amount),
        currency: NETWORK_CURRENCY[coin.network] || coin.coin,
        network: coin.network,
      });
      await api.post(`/credit-topups/${topup.id}/submit`, { txHash: hash.trim() });
      toast.success("Submitted — pending admin review.");
      onDone && onDone();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error((d && typeof d === "object" && d.message) || apiErrorMessage(e));
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid={`bc-step-deposit-${coin.network}`}>
      <button
        data-testid="bc-deposit-back"
        onClick={onBack}
        className="inline-flex items-center gap-1.5 border border-white/20 hover:border-[#F5B841] hover:text-[#F5B841] px-4 h-10 font-mono text-[11px] uppercase tracking-widest text-[#E8ECF4] transition-colors"
      ><ArrowLeft className="w-4 h-4" />Back to coins</button>

      <div className="grid grid-cols-1 md:grid-cols-[220px_1fr] gap-4 items-start">
        <div className="bg-white p-3 flex items-center justify-center" data-testid={`bc-deposit-qr-${coin.network}`}>
          <QRCodeSVG value={coin.address} size={192} level="M" />
        </div>
        <div className="space-y-3">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE]">Selected · Coin & Network</div>
            <div className="font-mono text-[#E8ECF4] mt-0.5" data-testid={`bc-deposit-label-${coin.network}`}>{coin.label}</div>
          </div>

          <div className="border border-white/15 p-3" data-testid={`bc-deposit-addr-${coin.network}`}>
            <div className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE] mb-1">Receiving address</div>
            <div className="font-mono text-xs text-[#E8ECF4] break-all">{coin.address}</div>
            <div className="mt-2"><CopyBtn text={coin.address} testid={`bc-deposit-addr-copy-${coin.network}`} label="Copy address" /></div>
          </div>

          {hasTag && (
            <div className="border-2 border-[#F5B841]/60 bg-[#F5B841]/[0.06] p-3" data-testid={`bc-deposit-tag-${coin.network}`}>
              <div className="flex items-center gap-2 mb-1">
                <AlertTriangle className="w-4 h-4 text-[#F5B841]" />
                <div className="text-[10px] font-mono uppercase tracking-widest text-[#F5B841]">Destination tag REQUIRED</div>
              </div>
              <div className="font-mono text-lg text-[#F5B841] tracking-wider" data-testid={`bc-deposit-tag-value-${coin.network}`}>{coin.destination_tag}</div>
              <div className="mt-2"><CopyBtn text={String(coin.destination_tag)} testid={`bc-deposit-tag-copy-${coin.network}`} label="Copy tag" /></div>
              <div className="text-[10px] text-[#F5B841] mt-2 leading-relaxed">Sending without the tag will result in your transfer landing in a shared wallet and require manual recovery.</div>
            </div>
          )}

          <div className="border-2 border-[#F26D6D] bg-[#F26D6D]/[0.06] p-3" data-testid={`bc-deposit-warning-${coin.network}`}>
            <div className="flex items-center gap-2 mb-1">
              <AlertTriangle className="w-4 h-4 text-[#F26D6D]" />
              <div className="text-[10px] font-mono uppercase tracking-widest text-[#F26D6D]">Network warning</div>
            </div>
            <div className="text-[11px] text-[#F26D6D] leading-relaxed">
              Send only <span className="font-mono font-bold">{coin.coin}</span> over <span className="font-mono font-bold">{coin.label}</span>. Sending another asset or using a different network can cause permanent loss of funds.
            </div>
          </div>
        </div>
      </div>

      <div className="border border-white/10 p-3 space-y-3" data-testid={`bc-deposit-form-${coin.network}`}>
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-[#F5B841]">Amount</div>
          <input
            data-testid={`bc-deposit-amount-${coin.network}`}
            type="text" inputMode="numeric" autoComplete="off"
            value={amount ? Number(amount).toLocaleString() : ""}
            onChange={(e) => setAmount(e.target.value.replace(/[^\d.]/g, ""))}
            placeholder="e.g. 5000"
            className="w-full mt-1 rounded-none bg-transparent border border-white/20 h-10 px-3 font-mono tabular-nums text-right"
          />
          <div className="text-[10px] font-mono text-[#8B97AE] leading-relaxed mt-1">
            Minimum transfer is <span className="text-[#E8ECF4]">${minUsd.toFixed(2)}</span> — smaller top-ups aren't practical (a player unlock costs more than that). Informational only — nothing is seized or withheld, this is guidance.
          </div>
        </div>

        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-[#F5B841]">Transaction hash</div>
          <input
            data-testid={`bc-deposit-hash-${coin.network}`}
            value={hash}
            onChange={(e) => setHash(e.target.value.slice(0, 200))}
            placeholder={coin.network.startsWith("USDT-ERC") || coin.network === "ETH" || coin.network === "USDC-ERC20" ? "0x…" : "Tx ID"}
            className="w-full mt-1 rounded-none bg-transparent border border-white/20 h-10 px-3 font-mono text-sm text-[#E8ECF4]"
          />
          <div className="text-[10px] font-mono text-[#8B97AE] mt-1 leading-relaxed">
            Paste the transaction hash from your wallet after sending — this is how we match your payment.
          </div>
          {hash.trim() && !hashOk && (
            <div className="text-[10px] font-mono text-[#F26D6D] mt-1" data-testid={`bc-deposit-hash-hint-${coin.network}`}>
              That doesn't look like a valid {coin.network} transaction hash. Double-check what your wallet gave you.
            </div>
          )}
        </div>

        <div className="flex justify-end">
          <button
            data-testid={`bc-deposit-submit-${coin.network}`}
            onClick={submit}
            disabled={busy || !amountOk || !hashOk}
            className="inline-flex items-center gap-1.5 px-4 h-10 bg-[#2DD4A7] text-[#04150F] font-mono text-[11px] uppercase tracking-widest hover:bg-[#F5B841] transition-colors disabled:opacity-40"
          >{busy ? "…" : "Complete purchase"} <ChevronRight className="w-3 h-3" /></button>
        </div>
      </div>
    </div>
  );
}

function TopupHistory({ rows }) {
  const { t } = useI18n();
  if (!rows.length) return <div className="text-xs text-[#8B97AE]" data-testid="bc-history-empty">{t("buyCredits.empty")}</div>;
  const badge = (s) => ({
    awaiting_transfer: "border-[#F5B841]/50 text-[#F5B841]",
    submitted: "border-[#F5B841]/50 text-[#F5B841]",
    confirmed: "border-[#2DD4A7]/50 text-[#2DD4A7]",
    rejected: "border-[#F26D6D]/50 text-[#F26D6D]",
  }[s] || "border-white/15 text-[#8B97AE]");
  // Iter 145 — "Open a ticket" on rejected / pending top-up rows.
  // Prefills subject + body with row context (When, Amount, Coin,
  // Network, full Tx Hash, rejection reason) and opens the existing
  // SupportTickets create modal via the global prefill hook.
  const openTicket = (r) => {
    const when   = (r.createdAt || "").slice(0, 16).replace("T", " ");
    const amount = Number(r.amount || 0).toLocaleString();
    const coin   = r.currency || NETWORK_CURRENCY[r.network] || r.network || "";
    const network = r.network || "";
    const txHash = r.txHash || r.txReference || ""; // Full — never truncated.
    const status = String(r.status || "").toUpperCase();
    const reason = r.adminNote || "";
    const subject = `Top-up issue — ${coin} ${amount} — ${status}`;
    const lines = [
      `Top-up reference:`,
      `• When: ${when}`,
      `• Amount: ${amount}`,
      `• Coin: ${coin}`,
      `• Network: ${network}`,
      `• Tx hash: ${txHash}`,
      `• Status: ${status}`,
    ];
    if (reason) lines.push(`• Admin note / rejection reason: ${reason}`);
    lines.push("", "Please help resolve this top-up. Details above.");
    const body = lines.join("\n");
    // Ensure the Support tab is visible before opening the modal. The
    // streamer dashboard renders SupportTickets inside a hidden tab
    // section until activated — clicking the tab button flips the
    // `hidden` attribute so the modal (rendered inside that section)
    // is actually displayed.
    try {
      document.querySelector('[data-testid="streamer-tabs-tab-support"]')?.click();
    } catch (e) { /* tab bar may vary; fall through */ }
    // Wait one frame so React commits the tab swap before opening.
    setTimeout(() => {
      if (typeof window.__vgOpenTicketPrefill === "function") {
        window.__vgOpenTicketPrefill({ subject, body });
      } else {
        // Fallback: pre-stage via localStorage and toast the user.
        try { localStorage.setItem("__vgTicketPrefill", JSON.stringify({ subject, body })); } catch (e) {}
        toast.message("Opening support…", { description: "Head to the Support tab and click Open a ticket." });
      }
    }, 60);
  };
  return (
    <table className="w-full text-xs" data-testid="bc-history-table">
      <thead className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE]">
        <tr><th className="text-left py-1">When</th><th className="text-left py-1">Amount</th><th className="text-left py-1">Coin</th><th className="text-left py-1">Status</th><th className="text-left py-1">Tx hash</th><th className="text-left py-1">Note / Outcome</th></tr>
      </thead>
      <tbody className="font-mono">
        {rows.map((r) => {
          const canTicket = r.status === "rejected" || r.status === "submitted" || r.status === "awaiting_transfer";
          const outcome = r.status === "confirmed"
            ? (typeof r.grantedAmount === "number" && r.grantedAmount !== r.amount
                ? <span className="text-[#F5B841]">Granted {Number(r.grantedAmount).toLocaleString()} (requested {Number(r.amount).toLocaleString()})</span>
                : <span className="text-emerald-400">Confirmed · {Number(r.grantedAmount ?? r.amount).toLocaleString()} credits</span>)
            : r.status === "rejected"
              ? <span className="text-[#F26D6D]" data-testid={`bc-history-reason-${r.id}`}>Rejected: {r.adminNote || "no reason recorded"}</span>
              : <span className="text-[#8B97AE]">—</span>;
          return (
            <tr key={r.id} className="border-t border-white/5" data-testid={`bc-history-row-${r.id}`}>
              <td className="py-1.5 text-[#8B97AE]">{(r.createdAt || "").slice(0, 16).replace("T", " ")}</td>
              <td className="py-1.5 text-[#E8ECF4]">{Number(r.amount || 0).toLocaleString()}</td>
              <td className="py-1.5 text-[#8B97AE]">{r.network}</td>
              <td className="py-1.5"><span className={`inline-flex border px-1.5 py-0.5 text-[9px] uppercase tracking-widest ${badge(r.status)}`}>{r.status}</span></td>
              <td className="py-1.5 text-[#8B97AE] truncate max-w-[240px]">{r.txHash || r.txReference || "—"}</td>
              <td className="py-1.5 max-w-[320px] break-words">
                <div>{outcome}</div>
                {canTicket && (
                  <button
                    type="button"
                    data-testid={`bc-history-ticket-${r.id}`}
                    onClick={() => openTicket(r)}
                    className="mt-1 inline-flex items-center gap-1 border border-[#2DD4A7]/50 text-[#2DD4A7] px-2 py-0.5 text-[10px] uppercase tracking-widest hover:bg-[#2DD4A7] hover:text-[#04150F] transition-colors"
                  >{t("support.openBtn")}</button>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

export default function BuyCredits({ onLedgerChange }) {
  const { t } = useI18n();
  const [coins, setCoins] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCoin, setSelectedCoin] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ci, hist] = await Promise.all([
        api.get("/credit-topups/deposit-info").catch(() => ({ data: { coins: [] } })),
        api.get("/credit-topups"),
      ]);
      setCoins(ci.data?.coins || []);
      setRows(hist.data || []);
    } catch (e) { /* silent */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const finishSubmit = async () => {
    setSelectedCoin(null);
    await load();
    onLedgerChange && onLedgerChange();
  };

  if (loading) return null;

  return (
    <section className="mt-8 border border-[#F5B841]/25 bg-[#F5B841]/[0.03] p-6 lg:p-8" data-testid="buy-credits-section">
      <div className="flex items-center justify-between gap-2 mb-1">
        <div className="flex items-center gap-2"><Coins className="w-4 h-4 text-[#F5B841]" /><h2 className="font-display font-bold text-lg">{t("buyCredits.title")}</h2></div>
        <button onClick={load} className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-[#8B97AE] hover:text-[#F5B841]" data-testid="bc-refresh"><RefreshCw className="w-3 h-3" />{t("buyCredits.refresh")}</button>
      </div>
      <p className="text-xs text-[#8B97AE] mb-4">{t("buyCredits.desc")}</p>

      {/* Iter 172 — Refund policy disclaimer. Visible BEFORE and DURING
          the purchase flow so a streamer sees the "credit-only refund"
          rule prior to sending funds. Display text only — no engine,
          no policy enforcement moved here; those live in the settlement
          + top-up code paths already. */}
      <div
        className="mb-5 border border-[#F5B841]/40 bg-[#F5B841]/[0.06] p-4 flex gap-3"
        data-testid="bc-refund-disclaimer"
      >
        <AlertTriangle className="w-4 h-4 text-[#F5B841] shrink-0 mt-0.5" />
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-[#F5B841] mb-1">
            {t("buyCredits.refundDisclaimer.title")}
          </div>
          <p className="text-xs text-[#E8ECF4] leading-relaxed">
            {t("buyCredits.refundDisclaimer.body")}
          </p>
        </div>
      </div>

      {!selectedCoin && <CoinPicker coins={coins} onPick={setSelectedCoin} />}
      {selectedCoin && (
        <DepositAndSubmit
          coin={selectedCoin}
          onBack={() => setSelectedCoin(null)}
          onDone={finishSubmit}
        />
      )}

      <div className="mt-6">
        <h3 className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE] mb-2">{t("buyCredits.yourTopups")}</h3>
        <TopupHistory rows={rows} />
      </div>
    </section>
  );
}
